import { createTheme } from '@mui/material/styles';
import colors from '../../styles/colors';

/**
 * CoachTools MUI theme — Avida palette.
 *
 * This used to declare only `primary.main`, which left every other MUI default
 * in place: stock Material blue for secondary/info, stock greys for dividers and
 * disabled states, and a white canvas. Those defaults are what made the dashboard
 * look like two palettes stacked on top of each other. The palette is now stated
 * in full so MUI-rendered chrome derives from the same tokens as our own
 * styled-components.
 *
 * Header note: MUI's AppBar defaults to `color="primary"`, so with lime as
 * primary the top bar would render lime. In the Avida design lime is reserved for
 * primary *actions* (Claim, Send) and the chrome is mint — hence the explicit
 * MuiAppBar override below rather than a different primary.
 */
const theme = createTheme({
  palette: {
    primary: {
      main: colors.base.primary, // lime
      light: colors.shades.primary3,
      dark: colors.shades.primary2,
      contrastText: colors.base.beta, // ink — lime is a light surface
    },
    secondary: {
      main: colors.base.secondary, // deep green
      dark: colors.shades.secondary1,
      contrastText: colors.base.alpha,
    },
    info: {
      main: colors.base.secondary,
      contrastText: colors.base.alpha,
    },
    // light/dark are deliberately omitted for the three Alert severities. MUI treats
    // palette.X.light as a tonal variant of main, not as a free slot for a pale tint:
    // Alert derives its text from darken(light, 0.6), its standard background from
    // lighten(light, 0.9), and the outlined border from light itself. Passing the pale
    // danger2 / warning1 tints flattened error and warning alerts to grey text on an
    // effectively white background (1.02:1), which undid the whole point of leaving the
    // semantic colours alone. warning.dark was also LIGHTER than warning.main, so a
    // contained warning button lightened on hover. augmentColor derives both correctly
    // from main via tonalOffset.
    success: {
      main: colors.shades.success6,
      contrastText: colors.base.alpha,
    },
    error: {
      main: colors.base.danger,
      contrastText: colors.base.alpha,
    },
    warning: {
      main: colors.base.warning,
      contrastText: colors.base.alpha,
    },
    background: {
      // Must NOT be base.gamma: 35 components use that token as their own panel fill,
      // so pointing the canvas at it made every one of them invisible (1:1) wherever it
      // sat on the canvas instead of inside a white card.
      default: colors.base.alpha, // page canvas
      paper: colors.base.alpha,
    },
    text: {
      primary: colors.base.beta, // ink
      secondary: colors.shades.gamma10,
      disabled: colors.shades.gamma2,
    },
    // Must be a DARK colour carrying alpha, not a light opaque one. MUI X DataGrid
    // derives its cell border as lighten(alpha(divider, 1), 0.88) — it assumes divider
    // is dark and lightens it. Handing it an already-light gamma1 produced #FBFBFB
    // (1.03:1) and every table row line disappeared. Ink at 12% renders as #E3E4E4 for
    // ordinary dividers AND lightens to #E3E4E4 for the grid, matching MUI's default.
    divider: colors.addAlpha(colors.base.beta, 0.12),
    action: {
      active: colors.shades.gamma10,
      // Translucent for the same reason as `divider` above: MUI applies these as raw
      // backgrounds (ListItem, MenuItem, Chip, Autocomplete, PaginationItem) and its own
      // defaults are rgba so they compose over whatever surface they land on. Opaque
      // values only work on light ones — the ink nav rail had to override them locally,
      // which was the tell. Chip in particular uses action.selected as its DEFAULT fill.
      hover: colors.addAlpha(colors.base.beta, 0.04),
      selected: colors.addAlpha(colors.base.beta, 0.08),
      disabled: colors.shades.gamma9,
      disabledBackground: colors.shades.gamma1,
    },
  },
  components: {
    MuiTabs: {
      styleOverrides: {
        indicator: {
          // Same trap as the spinner: MUI draws the indicator in primary.main, and lime
          // is a light *surface* colour — on a white tab strip it sat at ~1.2:1.
          backgroundColor: colors.base.beta,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          // MUI colours the selected label primary.main too, which made the active tab
          // the least readable one on the strip. Ink matches the treatment the app's own
          // styled tab components already use.
          '&.Mui-selected': {
            color: colors.base.beta,
          },
        },
      },
    },
    MuiCircularProgress: {
      styleOverrides: {
        colorPrimary: {
          // Lime is a light surface colour: as a spinner on white it measured 1.21:1,
          // below the 3:1 WCAG 1.4.11 asks of a non-text indicator. Deep green is the
          // palette's "actionable" tone and reaches 5.92:1.
          color: colors.base.secondary,
        },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          backgroundColor: colors.base.secondary3, // mint
          color: colors.base.beta, // ink — MUI would otherwise use background.default
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        colorPrimary: {
          backgroundColor: colors.base.secondary3, // mint chrome
          color: colors.base.beta,
          borderBottom: `1px solid ${colors.shades.secondary6}`,
          boxShadow: 'none',
        },
      },
    },
  },
  drawer: {
    width: 240,
  },
});

export default theme;
